/**
 * A new competition or challenge document — the ONE construction the admin
 * panel (`useCompetitionAdmin.createCompetition`) and the MCP server
 * (`create_competition_draft` / `create_challenge_draft`) both run.
 *
 * The MCP server writes with the Admin SDK, which bypasses the security rules.
 * If it built the document by hand, the rules would protect one writer's idea
 * of a draft and nothing would protect the other's; two copies drift. So the
 * shape lives here, and neither writer adds fields of its own beyond the
 * caller-owned ones below.
 *
 * Pure: no Firestore sentinel is created here. The caller owns
 *   - the document id (`"comp_" + Date.now()` on the client; the server picks
 *     its own) and the group ids (`doc(collection(...)).id` on the client, the
 *     Admin SDK's auto-id on the server — different algorithms),
 *   - `createdBy`, `createdAt`, `updatedAt` (`serverTimestamp()` is an SDK
 *     object, and the two SDKs have different ones),
 *   - the actor stamp (`lastModifiedBy`, and on MCP writes `lastModifiedVia` /
 *     `lastModifiedByClient`) and every group's `createdAt`.
 */
import { buildOrganizerContact } from "./organizerContact.js";
import { resolveGuestMode, resolveProxyGuestLimit } from "./registrationWindow.js";
import { DISCIPLINES } from "./disciplines.js";
import { gymDefaultLang, localizedFromInput } from "./localizedText.js";
import { challengeSource } from "./sprayChallenge.js";

/**
 * What a new competition's `settings` start from. `maxAttempts` stays as a
 * null field for backward-compat reads of older competitions — the admin
 * editor no longer exposes a competition-wide input; new competitions set the
 * cap per group.
 */
const DEFAULT_COMP_SETTINGS = {
  hasZones: true,
  maxAttempts: null,
  // What the scorecard holds: gym routes, or Spraywall problems (then every
  // route-id field below holds a problem id). Read via competitionSource().
  source: "routes",
  routeIds: [],
  groupRouteIds: {},
  mapId: null,
  scoringSystem: "tops_and_zones",
  pointPool: 1000,
  countBest: 10,
  flashBonus: null,
  routeCompPoints: {},
  groupRouteCompPoints: {},
  announcements: {
    enabled: false,
    warnings: [10, 5, 1],
    useTextToSpeech: false,
    customMessages: null,
  },
  useStartNumbers: false,
  entryFee: {
    enabled: false,
    amount: null,
    currency: "HUF",
    note: null,
  },
};

/**
 * A competition group document's fields — the ONE shape the admin editor's
 * `saveGroup` and the MCP draft tools write. Defaults = a fresh group: jam
 * mode, no manual display order (`sortIndex: null`), open to every level and
 * age, no fee or attempt override. The caller adds `createdAt` and the actor
 * stamp (SDK-specific).
 */
export function buildGroupDoc(input, order = 0) {
  const d = input || {};
  return {
    name: d.name || "",
    mode: d.mode || "jam",
    order: d.order ?? order,
    // THE display order (`sortGroupsForDisplay`); `order` above is legacy.
    sortIndex: Number.isFinite(d.sortIndex) ? d.sortIndex : null,
    // Advisory level cap (a difficulty KEY); null = open group.
    maxDifficulty: d.maxDifficulty || null,
    color: d.color || null,
    entryFeeAmount: d.entryFeeAmount ?? null,
    maxAttempts: d.maxAttempts ?? null,
    ageMin: d.ageMin ?? null,
    ageMax: d.ageMax ?? null,
    // PUBLIC (the groups doc is world-readable); ≤ 500 chars by the rules.
    description: d.description ?? null,
  };
}

/**
 * A competition session's base fields — what the admin editor's `saveSession`
 * and the MCP draft tools write. `startTime` is the editor's `datetime-local`
 * string ("YYYY-MM-DDTHH:mm", the gym's local time), not a Timestamp. The
 * caller owns `createdAt`, the actor stamp, `status` (a new session is
 * `scheduled`; an edit must not reset a running one) and the finals fields.
 */
export function buildSessionDoc(input, order = 0) {
  const d = input || {};
  return {
    name: d.name || "",
    groupIds: Array.isArray(d.groupIds) ? d.groupIds : [],
    startTime: d.startTime || null,
    durationMinutes: d.durationMinutes || 0,
    maxParticipants: d.maxParticipants || null,
    order: d.order ?? order,
  };
}

/**
 * The facts about a competition's groups and sessions that live denormalized on
 * the competition doc — `{ groupId: name }`, `{ sessionId: name }` and "is any
 * group judged" — so list cards resolve names without loading subcollections.
 * `groups` / `sessions`: arrays of `{ id, name, mode }`; entries without an id
 * are skipped.
 */
export function competitionDenorm(groups, sessions) {
  const groupNames = {};
  const sessionNames = {};
  for (const g of Array.isArray(groups) ? groups : []) if (g && g.id) groupNames[g.id] = g.name || g.id;
  for (const s of Array.isArray(sessions) ? sessions : []) if (s && s.id) sessionNames[s.id] = s.name || s.id;
  return {
    groupNames,
    sessionNames,
    hasJudgedGroups: (Array.isArray(groups) ? groups : []).some((g) => g && g.mode === "judged"),
  };
}

/**
 * Build a draft competition (or, with `input.type === "challenge"`, a draft
 * challenge) and its starting groups.
 *
 * Returns `{ doc, groups }`:
 *   - `doc` — the competition document without the caller-owned fields.
 *     `status` is ALWAYS `"draft"`: the input cannot set it (part of the
 *     prompt-injection defence — nothing built here is ever live). Only the
 *     fields listed are taken from the input; anything else is ignored.
 *   - `groups` — one group doc (`buildGroupDoc`) per element of `input.groups`
 *     (group objects, in order), or, when that is absent, one fresh group per
 *     name in `input.groupNames` (non-empty strings, trimmed, in the given
 *     order; `order` = position). The admin panel creates groups in the editor
 *     instead, so it passes neither and writes none.
 *
 * `proxyGuestLimit` is taken (clamped by `resolveProxyGuestLimit`) only when
 * the input names one — the app writes it on its own editor save.
 *
 * `gymCtx` is the gym the draft is for (`{ gymId, country }`). Its `country`
 * decides the default language the three organiser texts mirror
 * (`gymDefaultLang`). A `…I18n` map input is the whole text and the plain field
 * beside it only its mirror; without a map the plain `description` /
 * `longDescription` / `rules` means that language (`localizedFromInput`).
 * The discipline default is the platform one,
 * `boulder`, exactly as the editor writes it.
 */
export function buildCompetitionDraft(input, gymCtx) {
  const data = input || {};
  const lang = gymDefaultLang(gymCtx);
  const text = (field, opts) => localizedFromInput(data, field, lang, opts);
  const doc = {
    name: data.name || "",
    ...text("description"),
    ...text("longDescription"),
    ...text("rules", { empty: null }),
    type: data.type || "standard",
    status: "draft",
    startDate: data.startDate || "",
    endDate: data.endDate || "",
    registrationStart: data.registrationStart || null,
    registrationDeadline: data.registrationDeadline || null,
    registrationClosed: false,
    guestMode: resolveGuestMode(data),
    ...(data.proxyGuestLimit != null ? { proxyGuestLimit: resolveProxyGuestLimit(data) } : {}),
    // Mirror of "are there guests at all" for older clients.
    allowGuests: resolveGuestMode(data) !== "off",
    // Denormalized from the groups that are about to be written (template
    // paste, Excel import); the editor's own save corrects it afterwards.
    hasJudgedGroups: !!data.hasJudgedGroups,
    settings: { ...DEFAULT_COMP_SETTINGS, ...(data.settings || {}) },
    externalUrl: data.externalUrl || null,
    coverImageUrl: data.coverImageUrl || null,
    // Who to call. `null` when not given, and then every surface falls back to
    // the gym's own contact (`resolveOrganizerContact`).
    organizerContact: buildOrganizerContact(data.organizerContact),
    // A spray challenge is always boulder: a spray problem is a boulder
    // problem, and the TV round and the lead filter read `discipline`.
    discipline: data.type === "challenge" && challengeSource(data) === "spray"
      ? DISCIPLINES.BOULDER
      : (data.discipline || DISCIPLINES.BOULDER),
    // challenge-only top-level fields: auto participation + admin exclusion map
    // + the source (routes / spray), always written so a reader never guesses
    ...(data.type === "challenge" ? {
      participation: data.participation || "auto",
      excludedUsers: data.excludedUsers || {},
      excludedRoutes: data.excludedRoutes || {},
      source: challengeSource(data),
    } : {}),
  };
  const names = (Array.isArray(data.groupNames) ? data.groupNames : [])
    .filter((n) => typeof n === "string" && n.trim())
    .map((n) => n.trim());
  const groups = Array.isArray(data.groups)
    ? data.groups.filter((g) => g && typeof g === "object").map((g, i) => buildGroupDoc(g, i))
    : names.map((name, i) => buildGroupDoc({ name }, i));
  return { doc, groups };
}
